/* eslint-disable no-nested-ternary */
/* eslint-disable consistent-return */
import { Icon } from '@components';
import { useScreenSize } from '@hooks';
import { activityApi } from '@store';
import { Button, Flex, Image, theme, Typography } from '@styles';
import { ReactComponent as DoneIcon } from '@svgs/done.svg';
import { ReactComponent as EyeIcon } from '@svgs/eye.svg';
import { ActivitiesSideNavTemplate } from '@templates';
import parse, { domToReact } from 'html-react-parser';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

const parseOptions = {
  replace: (domNode) => {
    if (domNode.name === 'p' || domNode.name === 'li')
      return (
        <Typography
          color={theme.colors.white[100]}
          textAlign="justify"
          variant="MS-regular-s16-lh25"
        >
          {domNode.name === 'li' && '• '}
          {domToReact(domNode.children)}
        </Typography>
      );
  },
};

const ArticlePage = () => {
  const jwtAuthenticationToken = useSelector(
    (state) => state.authSlice.jwtAuthenticationToken
  );

  const { i18n } = useTranslation();
  const location = useLocation();
  const { isLaptop, isTabletLG } = useScreenSize();

  const { data, isLoading, refetch } = activityApi.useGetArticleByIdQuery(
    {
      id: location.state.articleId,
    },
    {
      skip: location.state?.isWithBooklet,
    }
  );

  const {
    data: dataWithBooklets,
    isLoading: isLoadingWithBooklets,
    refetch: refetchWithBooklets,
  } = activityApi.useGetArticleWithBookletsByIdQuery(
    {
      id: location.state.articleId,
    },
    {
      skip: !location.state?.isWithBooklet,
    }
  );

  const [markArticleAsRead] = activityApi.useMarkArticleAsReadMutation();

  useEffect(() => {
    if (location.state?.isWithBooklet) refetchWithBooklets?.();
    else refetch?.();
  }, [i18n.language]);

  const handleFinishReading = async () => {
    await markArticleAsRead({ articleId: location.state.articleId }).unwrap();
  };

  return (
    !(isLoading || isLoadingWithBooklets) && (
      <ActivitiesSideNavTemplate>
        <Flex flexDirection="column" mb={50} pl={isTabletLG ? 0 : 45}>
          <Flex
            alignItems="flex-end"
            {...(isTabletLG && { justifyContent: 'space-between' })}
            flexDirection="row"
            mb={5}
          >
            <Flex flexDirection="column" gap={10}>
              <Typography
                color={theme.colors.white[100]}
                variant="BN-bold-s44-lh44"
              >
                {(data || dataWithBooklets)?.titleText}
              </Typography>
              <Flex alignItems="center">
                <Icon color={theme.colors.white[100]} mr={2} SVG={EyeIcon} />
                <Typography
                  color={theme.colors.white[100]}
                  variant="MS-regular-s14-lh22"
                >
                  {(data || dataWithBooklets)?.wasSeenCount}
                </Typography>
              </Flex>
            </Flex>
            <Image
              alt="Article image"
              height="100%"
              maxHeight={isTabletLG ? 227 : 279}
              maxWidth={isTabletLG ? 343 : 421}
              mr={isLaptop ? -24 : 12}
              src={(data || dataWithBooklets)?.articleImage}
              width="100%"
            />
          </Flex>
          <Flex flexDirection="column" mb={50} pr={isLaptop ? 0 : 160}>
            {data && parse(data.content, parseOptions)}
            {dataWithBooklets && (
              <Typography
                color={theme.colors.schoolBusYellow[100]}
                variant="MS-regular-s16-lh25"
              >
                <a
                  href={dataWithBooklets.bookletLink}
                  rel="noreferrer"
                  style={{
                    color: theme.colors.schoolBusYellow[100],
                    textDecoration: 'none',
                  }}
                  target="_blank"
                >
                  {dataWithBooklets.bookletLink}
                </a>
              </Typography>
            )}
          </Flex>
          {jwtAuthenticationToken &&
            (data?.isRead ? (
              <Flex
                alignItems="center"
                backgroundColor={theme.colors.schoolBusYellow[100]}
                borderRadius={8}
                flexDirection="row"
                justifyContent="center"
                maxWidth={290}
                px={24}
                py={3}
                width="100%"
              >
                <Typography mr={2} variant="MS-semibold-s16-lh25">
                  {i18n.t('Activities.IsRead').toUpperCase()}
                </Typography>
                <Icon SVG={DoneIcon} />
              </Flex>
            ) : (
              <Button
                maxWidth={290}
                onClick={handleFinishReading}
                px={24}
                variant="secondary"
                width="100%"
              >
                <Typography
                  color={theme.colors.white[100]}
                  variant="MS-semibold-s16-lh25"
                >
                  {i18n.t('Activities.FinishRead').toUpperCase()}
                </Typography>
              </Button>
            ))}
        </Flex>
      </ActivitiesSideNavTemplate>
    )
  );
};
export default ArticlePage;
